import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface InstallPromptProps {
  className?: string;
}

const InstallPrompt: React.FC<InstallPromptProps> = ({ className = '' }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState<any>(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detectar si es iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Escuchar el evento beforeinstallprompt para dispositivos Android/Chrome
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevenir que Chrome muestre automáticamente la solicitud de instalación
      e.preventDefault();
      // Guardar el evento para usarlo más tarde
      setInstallPromptEvent(e);
      // Mostrar nuestra propia UI
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as any);

    // Verificar si la app ya está instalada
    const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches;
    if (isAppInstalled) {
      setShowPrompt(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as any);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPromptEvent) return;

    // Mostrar el prompt de instalación
    installPromptEvent.prompt();
    
    // Esperar a que el usuario responda al prompt
    const choiceResult = await installPromptEvent.userChoice;
    
    // Ocultar nuestro prompt independientemente de la elección del usuario
    setShowPrompt(false);
    setInstallPromptEvent(null);
    
    // Analítica (opcional)
    if (choiceResult.outcome === 'accepted') {
      console.log('Usuario aceptó la instalación de la app');
    } else {
      console.log('Usuario rechazó la instalación de la app');
    }
  };

  const handleDismiss = () => {
    // Guardar en localStorage que el usuario ha descartado el prompt
    localStorage.setItem('installPromptDismissed', 'true');
    setShowPrompt(false);
  };

  // No mostrar nada si el prompt no debe mostrarse o si ya fue descartado
  if (!showPrompt || localStorage.getItem('installPromptDismissed') === 'true') {
    return null;
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-lg p-4 z-50 border-t border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img src="/logo192.png" alt="DomiRed" className="w-10 h-10 rounded-lg" />
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Instala DomiRed</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {isIOS 
                ? 'Añade esta app a tu pantalla de inicio: toca el ícono de compartir y luego "Añadir a pantalla de inicio"' 
                : 'Instala esta app para acceder más rápido'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {!isIOS && (
            <button
              onClick={handleInstallClick}
              className="px-4 py-2 bg-domired-600 text-white rounded-lg font-medium hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500"
            >
              Instalar
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
